<template>
  <div>
    <v-autocomplete
      ref="autoCompleteInput"
      :data-intro="`<strong>${$t(
        'message.introJS.videos.titleSearch'
      )}</strong><br><br>${$t('message.introJS.videos.search')}`"
      :placeholder="placeholder"
      :items="filteredVideoList"
      :loading="loadingSearchBar"
      :append-icon="''"
      aria-autocomplete="off"
      autocomplete="off"
      class="autocomplete video-page"
      hide-selected
      hide-details
      no-filter
      dense
      solo
      flat
      @update:search-input="filterSearch"
      @keydown="handleKeyDown"
      @blur="handleSearchBlur"
      :menu-props="{
        value: searchedText && searchedText.length > 0,
        contentClass: 'video-page-autocomplete__content',
      }"
    >
      <template #prepend-inner>
        <v-icon size="16">panda-search</v-icon>
      </template>
      <template v-slot:no-data>
        <v-list-item>
          <v-list-item-title class="searchBarText">
            <span v-if="searchedText && searchedText.length > 0"
              >{{ $t('message.pages.videosDataTable.searchBar.noData') }}
              {{
                $t('message.pages.videosDataTable.searchBar.searched') +
                ' ' +
                searchedText
              }}</span
            >
            <span v-else>{{
              $t('message.pages.videosDataTable.searchBar.typeToSearch')
            }}</span>
          </v-list-item-title>
        </v-list-item>
      </template>
      <template v-slot:selection="{ attr, on, item, selected }">
        <span v-bind="attr" v-on="on" :input-value="selected">
          <span>{{ item.name ? item.name : item.title }}</span>
        </span>
      </template>
      <template v-slot:item="{ item }">
        <v-row no-gutters>
          <v-list-item
            class="row-search-item pa-0"
            @click="handleClickAutocompleteRow(item)"
          >
            <v-list-item-content class="py-0">
              <v-list-item-title>
                <v-row no-gutters class="d-flex flex-column">
                  <span
                    v-if="item.weight === 1"
                    class="body-regular-smaller setting-name_text-color text-truncate"
                    :title="item.setting_name"
                    v-html="highlightMatch(item.setting_name, item.indices)"
                  ></span>
                  <v-row no-gutters v-if="item.weight === 2">
                    <v-col cols="12">
                      <span
                        class="body-regular-smaller setting-name_text-color text-truncate"
                        :title="item.setting_name"
                        >{{ item.setting_name }}</span
                      >
                    </v-col>
                    <v-col cols="12">
                      <v-row no-gutters>
                        <v-icon size="12">panda-indent</v-icon>
                        <span
                          class="body-regular-smaller related-settings_text-color text-truncate ml-1"
                          :title="item.value"
                          :value="item.value + '(' + item.setting_name + ')'"
                          v-html="highlightMatch(item.value, item.indices)"
                        ></span>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-row>
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-row>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import Fuse from 'fuse.js'

export default {
  name: 'SearchBar',
  inject: ['showEmbed'],
  components: {},
  props: {
    fuseOptions: {
      required: true,
      type: Object,
    },
    list: {
      required: true,
    },
    placeholder: {
      type: String,
      default: '',
      required: false,
    },
    isPandaAiEnabledAndReady: {
      type: Boolean,
      required: true,
    },
  },
  watch: {
    list() {
      this.filterSearch(this.searchedText)
    },
  },
  data: () => ({
    searchedText: '',
    loadingSearchBar: false,
    filteredVideoList: [],
  }),
  mounted() {
    document.onkeydown = function (e) {
      if (e.key === 'f' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault()
      }
    }
    document.addEventListener('keydown', this.checkIfCtrlAWasPressed)
  },
  methods: {
    ...mapMutations('user', ['UPDATE_ROUTED_VIDEO', 'UPDATE_GLOBAL_SNACKBAR']),
    checkIfCtrlAWasPressed(event) {
      if (event.ctrlKey && event.key === 'a') {
        const inputElement = this.$refs.autoCompleteInput.$refs.input
        if (inputElement && this.searchedText.length > 0) {
          inputElement.select()
          event.preventDefault()
        }
      }
    },
    handleClickAutocompleteRow(item) {
      if (this.underlinedClick) return
      if (!item) return
      this.handleSettingRedirecting(item.setting_id)

      this.$refs.autoCompleteInput.clearableCallback()
      this.$refs.autoCompleteInput.isFocused = false
    },

    handleKeyDown(event) {
      if (event.keyCode === 27) {
        this.searchedText = ''
      }
      if (event.key === 'Backspace' || event.keyCode === 8) {
        this.$nextTick(() => {
          this.$refs.autoCompleteInput.isFocused = true
          setTimeout(() => {
            this.$refs?.autoCompleteInput?.focus()
          }, 100)
        })
      }
    },
    handleSearchBlur() {
      if (this.blockBlur || this.showMenu) return
      if (this.isClickingFilterContainer) {
        this.$refs.autoCompleteInput.focus()
        return
      }
      this.searchedText = ''
      this.filteredVideoList = []
      this.$root.$emit('resetIsAnyOtherModal', false)
    },
    filterSearch(typedValue) {
      this.loadingSearchBar = true
      this.searchedText = typedValue || ''
      if (typedValue && typedValue.length > 0) {
        const list = this.list
        const fuse = new Fuse(list, this.fuseOptions)
        let searchResult = fuse.search(typedValue)
        const itemsResults = this.analyzeMatches(searchResult)
        this.filteredVideoList = itemsResults
      } else {
        this.filteredVideoList = []
      }
      this.loadingSearchBar = false
    },
    analyzeMatches(searchResult) {
      return searchResult
        .flatMap((result) => {
          const { item, matches } = result

          let filteredMaches = []

          function checkIfNormalized(obj) {
            if (item.normalized_setting_name) {
              Object.assign(obj, {
                setting_name: item.setting_label,
              })
            } else {
              Object.assign(obj, {
                setting_name: item.setting_name,
              })
            }
            return obj
          }

          matches.forEach((m) => {
            filteredMaches.push(
              checkIfNormalized({
                key: m.key,
                value: m.value,
                setting_id: item.setting_id,
                weight: m.refIndex || m.refIndex === 0 ? 2 : 1,
                indices: m.indices,
              })
            )
          })

          return filteredMaches
        })
        .sort((a, b) => a.weight - b.weight)
    },
    handleSettingRedirecting(id) {
      const pandaAi_ids = ['mindmap', 'ebook', 'quiz']
      const analytics_ids = ['metrics', 'analytics']
      const isPandaAi = pandaAi_ids.includes(id)
      const isAnalytcs = analytics_ids.includes(id)
      const embedActions = {
        share: 0,
        embed: 1,
        performance: 2,
        hls: 3,
      }
      if (embedActions.hasOwnProperty(id)) {
        this.showEmbed(embedActions[id])
        return
      }
      if (isPandaAi && !this.isPandaAiEnabledAndReady) {
        this.$root.$emit('openAiWorkFlowModal')
        return
      }
      if (isAnalytcs) {
        this.$emit('openAnalyticsNewTab')
        this.handleSearchBlur()
        return
      }
      const $settingBtn = document.querySelector('#' + id)
      $settingBtn.click()
    },
    highlightMatch(text, indices) {
      if (!indices || indices.length === 0) return text
      const sortedIndices = [...indices].sort((a, b) => a[0] - b[0])

      let result = ''
      let lastIndex = 0

      for (const [start, end] of sortedIndices) {
        result += text.slice(lastIndex, start)
        for (let i = start; i <= end; i++) {
          const char = text[i]
          result += `<span class="mark-search" style="font-weight: 700;">${char}</span>`
        }
        lastIndex = end + 1
      }

      result += text.slice(lastIndex)
      return result
    },
  },
}
</script>
<style lang="scss">
.v-menu__content:has(.filter-click-container) {
  z-index: 8 !important;
}
.v-menu__content.video-page-autocomplete__content {
  .v-list {
    .v-list-item {
      margin: 0 !important;
      min-height: unset !important;
      &.v-list-item--link:before {
        transition: unset !important;
      }
      &:hover::before {
        background-color: varFigma(neutral-99) !important;
      }
      &:hover {
        background-color: varFigma(neutral-99) !important;
      }
      .v-list-item {
        padding: $spacing-04 $spacing-05 !important;
        height: auto !important;
        &.v-list-item--link:before {
          transition: unset !important;
        }
      }
    }
  }
}
</style>
<style lang="scss" scoped>
@import '@/assets/scss/src/autocomplete';

.setting-name_text-color {
  color: varFigma(neutral-40-light);
}
.related-settings_text-color {
  color: varFigma(neutral-50);
}

::v-deep
  .v-autocomplete:not(.v-input--is-disabled).v-select.v-text-field
  input {
  pointer-events: inherit !important;
  font-size: 12px !important;
}

::v-deep .v-text-field.v-text-field--solo.v-input--dense > .v-input__control {
  min-height: 32px !important;
}

::v-deep .panda-search:before {
  content: '\e90f';
  font-size: 16px;
}

::v-deep .autocomplete:hover {
  border: 1px solid varFigma(brand-80-light) !important;
}

::v-deep .autocomplete[class*='active'] {
  border: 1px solid varFigma(brand-80-light) !important;
}
::v-deep .autocomplete[class*='is-focused'] {
  border: 1px solid varFigma(brand-80-light) !important;
}

::v-deep .autocomplete[class*='active']:hover {
  border: 1px solid varFigma(brand-80-light) !important;
}

.searchBarText {
  color: varFigma(neutral-40-light);
  text-align: center;
  font-size: 12px !important;
  font-style: normal !important;
  font-weight: 400 !important;
  line-height: 150% !important; /* 18px */
}

.row-search-item {
  max-width: 100%;
}
</style>
